import { generateScheme, schemeLabel, type SchemeType } from '../lib/scheme';
import { toHex, type RGBA } from '../lib/color';

export interface SchemeRef {
  root: HTMLElement;
  update: (c: RGBA) => void;
  onPick: (fn: (c: RGBA) => void) => void;
}

const TYPES: SchemeType[] = [
  'complement',
  'triadic',
  'analogous',
  'split-complement',
  'shades',
  'tints',
  'tones'
];

export function createScheme(initial: RGBA): SchemeRef {
  const root = document.createElement('section');
  root.className = 'scheme';

  const tabs = document.createElement('div');
  tabs.className = 'scheme__tabs';
  const tabButtons: HTMLButtonElement[] = [];
  for (const type of TYPES) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'scheme__tab';
    btn.textContent = schemeLabel(type);
    btn.dataset.type = type;
    tabs.appendChild(btn);
    tabButtons.push(btn);
  }

  const swatches = document.createElement('div');
  swatches.className = 'scheme__swatches';

  let pickFn: ((c: RGBA) => void) | null = null;
  let currentType: SchemeType = 'complement';
  let currentBase: RGBA = initial;

  function render(): void {
    tabButtons.forEach((b) => {
      b.classList.toggle('scheme__tab--active', b.dataset.type === currentType);
      b.textContent = schemeLabel(b.dataset.type as SchemeType);
    });
    swatches.replaceChildren();
    const colors = generateScheme(currentBase, currentType);
    colors.forEach((c) => {
      const cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'scheme__cell';
      cell.style.background = c.a < 1 ? `rgba(${c.r},${c.g},${c.b},${c.a})` : toHex(c);
      cell.title = toHex(c);
      cell.setAttribute('aria-label', `Pick ${toHex(c)}`);
      cell.addEventListener('click', () => {
        if (pickFn) pickFn(c);
      });
      swatches.appendChild(cell);
    });
  }

  tabButtons.forEach((b) => {
    b.addEventListener('click', () => {
      currentType = b.dataset.type as SchemeType;
      render();
    });
  });

  function update(c: RGBA): void {
    currentBase = c;
    render();
  }

  function onPick(fn: (c: RGBA) => void): void {
    pickFn = fn;
  }

  root.append(tabs, swatches);
  render();

  return { root, update, onPick };
}
